import config from '../../config';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { formatDateStringToUTC, getExclusiveEndDate } from '../../util/dates';
import { storableError } from '../../util/errors';
import { getLocaleFromPath } from '../../util/locale';
import { logError } from '../../util/log';
import { getDistanceToListing, hasLocationPermission, userLocation } from '../../util/maps';
import { isCordova } from '../../util/mobile/deviceDetection';
import { amountOfSeatsSearchParams, plusListingPriceSearchParams } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import reducerRegistry from '../../ducks/reducerRegistry';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = isCordova() ? 50 : 100;

// ================ Action types ================ //

export const SEARCH_SET_TAB = 'app/SearchPage/SEARCH_SET_TAB';

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

// export const SEARCH_MAP_GET_GEOLOCATION_JSON_REQUEST =
//   'app/SearchPage/SEARCH_MAP_GET_GEOLOCATION_JSON_REQUEST';
// export const SEARCH_MAP_GET_GEOLOCATION_JSON_SUCCESS =
//   'app/SearchPage/SEARCH_MAP_GET_GEOLOCATION_JSON_SUCCESS';
// export const SEARCH_MAP_GET_GEOLOCATION_JSON_ERROR =
//   'app/SearchPage/SEARCH_MAP_GET_GEOLOCATION_JSON_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

// ================ Reducer ================ //

/** @type {import('../../../types/vansite').VanSite.State.SearchPage} */
const initialState = {
  tab: 'map',
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  lastQuery: '',
  lastLocale: '',
  activeListingId: null,
};

const resultIds = (data) => data.data.map((l) => l.id);

const reducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_SET_TAB:
      return { ...state, tab: payload };

    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
        lastQuery: payload.lastQuery,
        lastLocale: payload.lastLocale,
      };
    case SEARCH_LISTINGS_ERROR:
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return { ...state, activeListingId: payload };

    // case SEARCH_MAP_GET_GEOLOCATION_JSON_REQUEST:
    //   return { ...state };
    // case SEARCH_MAP_GET_GEOLOCATION_JSON_SUCCESS:
    //   return { ...state, geolocationJson: payload };
    // case SEARCH_MAP_GET_GEOLOCATION_JSON_ERROR:
    //   return { ...state, error: true, geolocationJsonError: payload };

    default:
      return state;
  }
};

export default reducer;

// ================ Action creators ================ //

export const setSearchPageTab = (tab) => ({
  type: SEARCH_SET_TAB,
  payload: tab,
});

export const searchListingsRequest = (searchParams) => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = ({ response, lastQuery }) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data, lastQuery },
});

export const searchListingsError = (e) => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const setActiveListing = (listingId) => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

// export const getGeolocationJsonRequest = () => ({ type: SEARCH_MAP_GET_GEOLOCATION_JSON_REQUEST });
// export const getGeolocationJsonSuccess = payload => ({
//   type: SEARCH_MAP_GET_GEOLOCATION_JSON_SUCCESS,
//   payload,
// });
// export const getGeolocationJsonError = e => ({
//   type: SEARCH_MAP_GET_GEOLOCATION_JSON_ERROR,
//   payload: e,
// });

const isClient = () => typeof window !== 'undefined';

// A failed/mangled response can arrive without data.data (VANSITE-FRONTEND-4KJ).
const listingsOf = (response) => response?.data?.data ?? [];

/**
 * Stamp `attributes.distance` on the listings in place. Without a known
 * location (or on the server) getDistanceToListing yields `false`.
 * @param {Array<object>} listings
 */
const stampDistances = (listings) => {
  for (const listing of listings) {
    listing.attributes.distance =
      listing.attributes.geolocation && getDistanceToListing(listing.attributes.geolocation);
  }
};

// One shared request, so concurrent searches (the landing page fires several)
// wait for the same position. A failure is not cached: the next search asks
// again, as before.
let pendingUserLocation = null;

const locateUser = () => {
  if (!pendingUserLocation) {
    pendingUserLocation = userLocation().then(
      (location) => {
        pendingUserLocation = null;
        window.currentLocation = location;
        return location;
      },
      (error) => {
        pendingUserLocation = null;
        throw error;
      }
    );
  }
  return pendingUserLocation;
};

/**
 * A sparse entities payload that carries nothing but the distance.
 * @param {Array<object>} listings
 */
const distanceOnlyResponse = (listings) => ({
  data: {
    data: listings
      .filter((listing) => listing.attributes.geolocation)
      .map(({ id, type, attributes }) => ({
        id,
        type,
        attributes: { distance: getDistanceToListing(attributes.geolocation) },
      })),
  },
});

/**
 * Resolve the user's location in the background and, once known, give the
 * already stored listing cards their distance badge, only once the location
 * permission is granted. Never blocks the search: geolocation can take up to
 * 5 s, and a late search response used to overwrite the full listing that the
 * ListingPage had stored in the meantime.
 * Only listings still in the store get the update: after a logout reset it
 * would otherwise insert bare entities that carry nothing but `distance`.
 * @param {Array<object>} listings
 */
const addDistancesInBackground = (listings) => async (dispatch, getState) => {
  if (!isClient() || window.currentLocation) return;
  // The badge is a passive extra: it never opens the location prompt. The landing page runs
  // these searches on app start, before the app's onboarding message asks for the location.
  if (!(await hasLocationPermission())) return;
  locateUser().then(
    () => {
      const stored = getState().marketplaceData?.entities?.listing || {};
      const update = distanceOnlyResponse(listings.filter((listing) => stored[listing.id.uuid]));
      if (update.data.data.length > 0) {
        dispatch(addMarketplaceEntities(update, { sparse: true }));
      }
    },
    () => {
      // Ignore this error because it is not critical
    }
  );
};

export const searchListings =
  (searchParams, locale = config.locale) =>
  async (dispatch, getState, sdk) => {
    const priceSearchParams = (priceParam) => {
      const inSubunits = (value) =>
        convertUnitToSubUnit(value, unitDivisor(config.currencyConfig.currency));
      const values = priceParam ? priceParam.split(',') : [];
      return priceParam && values.length === 2
        ? {
            price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
          }
        : {};
    };
    const datesSearchParams = (datesParam) => {
      const values = datesParam ? datesParam.split(',') : [];
      const hasValues = datesParam && values.length === 2;
      const startDate = hasValues ? values[0] : null;
      const isNightlyBooking = config.bookingUnitType === 'line-item/night';
      const endDate =
        hasValues && isNightlyBooking
          ? values[1]
          : hasValues
            ? getExclusiveEndDate(values[1])
            : null;

      return hasValues
        ? {
            start: formatDateStringToUTC(startDate),
            end: formatDateStringToUTC(endDate),
            // Availability can be full or partial. Default value is full.
            availability: 'full',
          }
        : {};
    };

    /** @type {CurrentUser} */
    const currentUser = getState().user.currentUser;
    const annualPass = currentUser && currentUser.attributes.profile.privateData.annualPass;

    const {
      perPage,
      price,
      dates,
      plus_listing,
      no_plus_listing,
      pub_amountOfSeats,
      ...rest
    } = searchParams;
    // The Plus toggles are a price range derived from the Plus threshold, so
    // they override any user-selected price band.
    const priceMaybe = {
      ...priceSearchParams(price),
      ...plusListingPriceSearchParams(plus_listing, no_plus_listing),
    };

    const datesMaybe = datesSearchParams(dates);
    const defaultSort = searchParams.keywords
      ? null
      : searchParams.sort
        ? searchParams.sort
        : !currentUser || !annualPass
          ? 'pub_verified,meta_rating'
          : 'pub_verified,-price';

    const params = {
      ...rest,
      ...priceMaybe,
      ...datesMaybe,
      ...amountOfSeatsSearchParams(pub_amountOfSeats),
      per_page: perPage,
      ...(defaultSort ? { sort: defaultSort } : {}),
    };
    if (params.tab) {
      delete params.tab;
    }

    // we don't need to search again when the query is the same
    // happens on mobile when using the go back button
    const lastQuery = getState().SearchPage.lastQuery;
    if (JSON.stringify({ ...params, locale }) === lastQuery) return;

    try {
      dispatch(searchListingsRequest(searchParams));
      const response = await sdk.listings.query(params); // todo: if loading=true or url change
      const listings = listingsOf(response);
      stampDistances(listings);
      // Card fieldset only — merge, never replace what a full fetch stored.
      dispatch(addMarketplaceEntities(response, { sparse: true }));
      dispatch(
        searchListingsSuccess({ response, lastQuery: JSON.stringify({ ...params, locale }) })
      );
      dispatch(addDistancesInBackground(listings));
      return response;
    } catch (e) {
      logError(e, 'search-listings-failed');
      dispatch(searchListingsError(storableError(e)));
    }
  };

export const loadData = (params, search, match) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const locale = getLocaleFromPath(match.path);
  const { page = 1, origin, ...rest } = queryParams;
  const originMaybe = config.sortSearchByDistance && origin ? { origin } : {};

  return searchListings(
    {
      ...rest,
      ...originMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['images'],
      'fields.listing': [
        'title',
        'state',
        'geolocation',
        'price',
        'createdAt',
        'publicData.category',
        'publicData.locationPlace',
        'publicData.verified',
        'metadata.rating',
        'metadata.ratingCount',
        'metadata.explorerListing',
        'metadata.annualPassPrice',
      ],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
      'limit.images': 1,
      pub_hidden: false,
    },
    locale
  );
};

// Injected lazily: the reducer reaches the store when this (code-split) module is evaluated.
reducerRegistry.register('SearchPage', reducer);
